/**
 * One league's section on the Scores home (Scores UX spec v1.0 section 2): the
 * shared section card with the league's short name, a live count while games
 * are in play and a "See all" link that opens the league view, then up to five
 * score rows separated by hairlines. Also carries the offseason
 * fallback, so a league between seasons shows last season's games instead
 * of an empty section (`scores/overview.md` rule 13, Q279). A failed
 * fallback read shows the failed line and Try again rather than hiding the
 * league (ISSUE-1022).
 *
 * A league the catalog marks coming soon has no games to fall back to
 * (ISSUE-1020). The fallback stays off for it, so the home does not read
 * last-played or a slate, and a failed read cannot raise the error dialog
 * over the league menu.
 */
import {
  useEventLastPlayedGet,
  useEventList,
} from "@sportsmet/events-sdk/private";
import { cn, FOCUS_RING, HIT_SLOP } from "@sportsmet/ui";
import { type ReactNode } from "react";

import { queryApiErrorIdentity } from "../../errors/apiErrorBus";
import { ChevronRightIcon } from "../../icons/glyphs";
import { SEE_ALL_LABEL, seeAllLabel } from "./leagueSelectorContract";
import { ScoreRow } from "./ScoreRow";
import type { typeScoresEventInput } from "./scoreRowModel";
import {
  ScoresSectionFailed,
  ScoresSectionLoading,
} from "./ScoresSectionStates";
import { countInPlay, ScoresSectionCard } from "./ScoresSectionCard";
import { useRaiseReadError } from "./useRaiseReadError";

export type typeLeagueSectionCardProps = {
  leagueId: string;
  /** The catalog's `shortName` -- "NFL", "MLB", "PGA Tour". The frames set
   *  the league name as TEXT, never as a mark: there is no licensed league
   *  logo. */
  leagueName: string;
  /** resolveViewerTimeZone() from the pane above. Never resolved here, so
   *  one render pass cannot use two different zones. */
  timeZone: string;
  /** This league's events from POST /feed/upcoming, already ordered by the
   *  server. May be empty, which is what triggers the offseason fallback. */
  events: readonly typeScoresEventInput[];
  /** True while the feed read that produced `events` is still in flight. */
  isFeedLoading: boolean;
  /** True only for a league the fan has pinned. Only a pinned league falls
   *  back to last season. */
  isPinned: boolean;
  /** Catalog `visibilityState === "comingSoon"` (ISSUE-1020). A coming soon
   *  league never runs the offseason fallback. */
  isComingSoon?: boolean;
  onOpenLeague: (leagueId: string) => void;
  onOpenGame?: ((eventId: string) => void) | undefined;
  /** The fan's followed teams, for the inline star on their rows. */
  favoriteTeamIds?: readonly string[] | undefined;
};

/** The header's "See all" link button: caption weight in `primary` with a
 *  chevron, a 36px visible box padded to a 44px target. */
function SeeAllButton(props: {
  leagueName: string;
  onOpen: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={cn(
        "relative flex h-chip-height items-center gap-1 rounded-sm px-2 text-caption text-primary",
        "transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none",
        HIT_SLOP,
        FOCUS_RING,
      )}
      data-testid="league-section-see-all"
      aria-label={seeAllLabel(props.leagueName)}
      onClick={props.onOpen}
    >
      {SEE_ALL_LABEL}
      <ChevronRightIcon />
    </button>
  );
}

export const LeagueSectionCard = (
  props: typeLeagueSectionCardProps,
): ReactNode => {
  const needsFallback =
    props.isPinned &&
    props.isComingSoon !== true &&
    !props.isFeedLoading &&
    props.events.length === 0;

  const lastPlayed = useEventLastPlayedGet(
    { leagueId: props.leagueId, timeZone: props.timeZone },
    { enabled: needsFallback },
  );
  const lastPlayedDay = lastPlayed.data?.data?.lastPlayedDay ?? null;

  const lastSlate = useEventList(
    {
      leagueId: props.leagueId,
      day: lastPlayedDay ?? "",
      timeZone: props.timeZone,
    },
    { enabled: needsFallback && lastPlayedDay !== null },
  );

  // Raise only while the fallback is needed, so a stale cached error from an
  // earlier fallback never raises once the feed carries games again.
  const lastPlayedError = useRaiseReadError({
    data: needsFallback ? lastPlayed.data : undefined,
    dataUpdatedAt: lastPlayed.dataUpdatedAt,
    identity: queryApiErrorIdentity(
      "scores-home",
      "event/last-played-get",
      props.leagueId,
    ),
  });
  const lastSlateError = useRaiseReadError({
    data: needsFallback && lastPlayedDay !== null ? lastSlate.data : undefined,
    dataUpdatedAt: lastSlate.dataUpdatedAt,
    identity: queryApiErrorIdentity(
      "scores-home",
      "event/list",
      props.leagueId,
      lastPlayedDay,
    ),
  });

  const isLastPlayedFailed =
    needsFallback && (lastPlayed.isError || lastPlayedError.errorCode !== null);
  const isLastSlateFailed =
    needsFallback &&
    lastPlayedDay !== null &&
    (lastSlate.isError || lastSlateError.errorCode !== null);
  const isFallbackFailed = isLastPlayedFailed || isLastSlateFailed;

  const isFallbackLoading =
    needsFallback &&
    (lastPlayed.isLoading || (lastPlayedDay !== null && lastSlate.isLoading));

  const hasEverPlayed = needsFallback
    ? (lastPlayed.data?.data?.hasEverPlayed ?? true)
    : true;

  const rows: readonly typeScoresEventInput[] =
    props.events.length > 0
      ? props.events
      : (lastSlate.data?.data?.events ?? []).slice(0, 5);

  const phase: "loading" | "failed" | "neverPlayed" | "noGames" | "ready" =
    props.isFeedLoading || isFallbackLoading
      ? "loading"
      : isFallbackFailed
        ? "failed"
        : needsFallback && !hasEverPlayed
          ? "neverPlayed"
          : rows.length === 0
            ? "noGames"
            : "ready";

  // Refetch only the reads that failed: a disabled last slate read would
  // fire with an empty day.
  const onRetryFallback = (): void => {
    if (isLastPlayedFailed) {
      lastPlayedError.forgetError();
      void lastPlayed.refetch();
    }
    if (isLastSlateFailed) {
      lastSlateError.forgetError();
      void lastSlate.refetch();
    }
  };

  const frame = (children: ReactNode, liveCount = 0): ReactNode => (
    <ScoresSectionCard
      testId="league-section"
      dataAttributes={{ "data-league-id": props.leagueId }}
      title={props.leagueName}
      titleTestId="league-section-title"
      liveCount={liveCount}
      trailing={
        phase === "ready" ? (
          <SeeAllButton
            leagueName={props.leagueName}
            onOpen={() => {
              props.onOpenLeague(props.leagueId);
            }}
          />
        ) : undefined
      }
    >
      {children}
    </ScoresSectionCard>
  );

  if (phase === "loading") {
    return frame(<ScoresSectionLoading testId="league-section-loading" />);
  }

  if (phase === "failed") {
    return frame(
      <ScoresSectionFailed
        testId="league-section-failed"
        onRetry={onRetryFallback}
      />,
    );
  }

  if (phase === "neverPlayed" || phase === "noGames") {
    return null;
  }

  return frame(
    rows.map((event) => (
      <ScoreRow
        key={event.eventId}
        event={event}
        onOpenGame={props.onOpenGame}
        favoriteTeamIds={props.favoriteTeamIds}
      />
    )),
    countInPlay(rows),
  );
};
